import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/ui-kit";

import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import DemoLogin from "@/pages/DemoLogin";
import PublicDiagnostic from "@/pages/PublicDiagnostic";
import PublicMilestone from "@/pages/PublicMilestone";
import ConfirmLead from "@/pages/ConfirmLead";
import Unsubscribe from "@/pages/Unsubscribe";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import StaticPage from "@/pages/StaticPage";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import AccessibilityButton from "@/components/AccessibilityButton";
import UpgradePrompt from "@/components/UpgradePrompt";
import { AccessibilityProvider } from "@/context/AccessibilityContext";
import ParentDashboard from "@/pages/ParentDashboard";
import Admin from "@/pages/Admin";
import StudentShell from "@/pages/student/StudentShell";
import Today from "@/pages/student/Today";
import Diagnostic from "@/pages/student/Diagnostic";
import Path from "@/pages/student/Path";
import Lesson from "@/pages/student/Lesson";
import Practice from "@/pages/student/Practice";
import Review from "@/pages/student/Review";
import Tutor from "@/pages/student/Tutor";
import Progress from "@/pages/student/Progress";
import Settings from "@/pages/student/Settings";

function RoleHome() {
  const { user } = useAuth();
  const impersonating = !!user.impersonated_by;
  // While impersonating, never enter the admin console — reflect the target's normal view.
  if (user.role === "admin" && !impersonating) return <Navigate to="/admin" replace />;
  if (user.role === "student") return <Navigate to="/me" replace />;
  return <Navigate to="/parent" replace />;
}

// Student user: redirect to their own student profile
function MyStudent() {
  const { user } = useAuth();
  const [id, setId] = React.useState(null);
  const [err, setErr] = React.useState(false);
  React.useEffect(() => {
    import("@/lib/api").then(({ default: api }) =>
      api.get("/students").then(({ data }) => {
        if (data.length) setId(data[0].id);
        else setErr(true);
      }).catch(() => setErr(true))
    );
  }, [user]);
  if (err) return <div className="p-8 text-center text-[var(--ns-muted)]">No student profile linked to this account.</div>;
  if (!id) return <Spinner label="Opening your space" />;
  return <Navigate to={`/student/${id}/today`} replace />;
}

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <Spinner label="Loading" />;
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  const { user, ready } = useAuth();
  // Process the Google OAuth callback BEFORE any protected route/redirect runs.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <ImpersonationBanner />
      <UpgradePrompt />
      <Routes>
      <Route path="/" element={!ready ? <Spinner /> : user ? <RoleHome /> : <Landing />} />
      <Route path="/auth" element={user ? <RoleHome /> : <Auth />} />
      <Route path="/demo" element={<DemoLogin />} />
      <Route path="/diagnostic" element={<PublicDiagnostic />} />
      <Route path="/m/:token" element={<PublicMilestone />} />
      <Route path="/confirm" element={<ConfirmLead />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/privacy" element={<StaticPage slug="privacy" />} />
      <Route path="/terms" element={<StaticPage slug="terms" />} />
      <Route path="/about" element={<StaticPage slug="about" />} />
      <Route path="/contact" element={<StaticPage slug="contact" />} />
      <Route path="/home" element={<Protected><RoleHome /></Protected>} />
      <Route path="/me" element={<Protected><MyStudent /></Protected>} />
      <Route path="/parent" element={<Protected><ParentDashboard /></Protected>} />
      <Route path="/admin" element={<Protected><Admin /></Protected>} />

      <Route path="/student/:studentId" element={<Protected><StudentShell /></Protected>}>
        <Route index element={<Navigate to="today" replace />} />
        <Route path="today" element={<Today />} />
        <Route path="diagnostic" element={<Diagnostic />} />
        <Route path="path" element={<Path />} />
        <Route path="lesson/:skillId" element={<Lesson />} />
        <Route path="practice/:skillId" element={<Practice mode="practice" />} />
        <Route path="mastery/:skillId" element={<Practice mode="mastery" />} />
        <Route path="review" element={<Review />} />
        <Route path="tutor" element={<Tutor />} />
        <Route path="tutor/:skillId" element={<Tutor />} />
        <Route path="progress" element={<Progress />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <AccessibilityProvider>
        <BrowserRouter>
          <AppRoutes />
          <AccessibilityButton />
        </BrowserRouter>
      </AccessibilityProvider>
    </div>
  );
}
